/* Funzioni pure in cima, l'elemento in fondo. I test ritagliano tutto quello
   che sta sopra la dichiarazione della classe e ci si agganciano cercandone il
   testo: non nominarla in nessun commento qui sopra, o il ritaglio finirebbe
   prima del codice. */

const DEG = Math.PI / 180;

// --- Countdown ---

const DAY = 86400000;
const HOUR = 3600000;
const MINUTE = 60000;

/**
 * La scadenza scritta dalla Liquid, in millisecondi. Accetta solo l'ISO con
 * fuso che esce dal filtro `date`: una data impossibile (31 febbraio) il filtro
 * la restituisce tale e quale, e `new Date()` su quella stringa da' un giorno
 * diverso in Chrome e NaN in Safari. Meglio rifiutarla ovunque.
 */
function parseExpires(value) {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}$/.test(value || '')) return NaN;
  return new Date(value).getTime();
}

/** Quanto manca a `targetMs`, in giorni, ore, minuti e secondi. Mai negativo. */
function remaining(targetMs, nowMs) {
  const left = Math.max(0, targetMs - nowMs);
  return {
    d: Math.floor(left / DAY),
    h: Math.floor((left % DAY) / HOUR),
    m: Math.floor((left % HOUR) / MINUTE),
    s: Math.floor((left % MINUTE) / 1000),
  };
}

function pad(n) {
  return String(n).padStart(2, '0');
}

// --- Geometria ---

/** polar2Cartesian di three-globe, in gradi: (0,0) guarda +z, (0,90) e' +x, il nord e' +y. */
function latLngToVec3(lat, lng, r = 1) {
  const la = lat * DEG;
  const ln = lng * DEG;
  return [r * Math.cos(la) * Math.sin(ln), r * Math.sin(la), r * Math.cos(la) * Math.cos(ln)];
}

/**
 * Dalla camera alla Terra: M = Ry(lng) · Rx(-lat), con lat e lng del punto al
 * centro del globo, in radianti. M·(0,0,1) e' quel punto, M·(0,1,0) punta a
 * nord. Per colonne, come vuole uniformMatrix3fv.
 */
function rotationMatrix(lat, lng) {
  const cp = Math.cos(lat);
  const sp = Math.sin(lat);
  const cl = Math.cos(lng);
  const sl = Math.sin(lng);
  return new Float32Array([cl, 0, -sl, -sl * sp, cp, -cl * sp, sl * cp, sp, cl * cp]);
}

const LIGHT_RADIUS = 1.0055; // 100.55 del prototipo, in raggi: le luci sfiorano la superficie

/** I centri delle luci, gli stessi del prototipo. */
const HUBS = {
  northAmerica: [
    [40.71, -74.0], [34.05, -118.24], [41.88, -87.63], [29.76, -95.37], [43.65, -79.38],
    [49.28, -123.12], [19.43, -99.13], [25.76, -80.19], [37.77, -122.42], [45.5, -73.57],
  ],
  southAmerica: [
    [-23.55, -46.63], [-22.91, -43.17], [-34.6, -58.38], [-12.05, -77.04], [4.71, -74.07],
    [-33.45, -70.67], [-0.18, -78.47], [-16.49, -68.12], [-8.05, -34.9],
  ],
  europe: [
    [51.51, -0.13], [48.86, 2.35], [52.52, 13.4], [41.9, 12.5], [40.42, -3.7], [52.37, 4.9], [50.08, 14.44],
    [59.33, 18.07], [59.91, 10.75], [55.68, 12.57], [45.46, 9.19], [48.21, 16.37], [38.72, -9.14],
  ],
  africa: [
    [30.04, 31.24], [33.59, -7.62], [6.52, 3.38], [5.6, -0.19], [-1.29, 36.82],
    [-6.79, 39.21], [-26.2, 28.05], [-33.92, 18.42], [9.03, 38.74], [14.72, -17.47],
  ],
  asia: [
    [35.68, 139.65], [31.23, 121.47], [39.9, 116.4], [37.57, 126.98], [1.35, 103.82], [13.76, 100.5], [19.08, 72.88],
    [28.61, 77.21], [25.2, 55.27], [24.71, 46.68], [41.01, 28.98], [55.76, 37.62], [14.6, 120.98], [21.03, 105.85],
  ],
  oceania: [
    [-33.87, 151.21], [-37.81, 144.96], [-27.47, 153.03], [-31.95, 115.86], [-36.85, 174.76], [-41.29, 174.78],
  ],
};

/** Quante luci per centro: il prototipo ne mette di piu' dove le citta' sono fitte, 9 altrove. */
const CLUSTER = { asia: 14, europe: 12 };

/**
 * Le luci attorno ai centri, distribuite come nel prototipo, ma convertite con
 * latLngToVec3: la conversione del prototipo dava z = -x e le metteva tutte su
 * un meridiano. Sei float per luce: x, y, z, fase, velocita', dimensione.
 */
function buildLights(rnd = Math.random) {
  const out = [];
  for (const [continent, hubs] of Object.entries(HUBS)) {
    const size = CLUSTER[continent] || 9;
    for (const [lat, lng] of hubs) {
      for (let i = 0; i < size; i++) {
        // Quasi tutte vicine al centro: il raggio va con una potenza, fino a 2,4°.
        const angle = rnd() * Math.PI * 2;
        const radius = Math.pow(rnd(), 1.8) * 2.4;
        out.push(
          ...latLngToVec3(lat + Math.sin(angle) * radius, lng + Math.cos(angle) * radius, LIGHT_RADIUS),
          rnd() * Math.PI * 2,
          0.45 + rnd() * 1.2,
          0.55 + rnd() * 0.85,
        );
      }
    }
  }
  return new Float32Array(out);
}

// --- Camera e rotazione ---

const CAM_DIST = 3.25; // 100 · (1 + altitude 2.25) del prototipo, in raggi
const FOV = 50 * DEG; // la PerspectiveCamera di default di globe.gl

/** Nel prototipo la silhouette del globo occupa questa frazione di meta' altezza della finestra: 0,6935. */
const SILHOUETTE = Math.tan(Math.asin(1 / CAM_DIST)) / Math.tan(FOV / 2);

/**
 * OrbitControls ruota di 2π · rotateSpeed per ogni altezza di finestra
 * trascinata, e globe.gl mette rotateSpeed = altitude · 0.3. Riportato al
 * raggio del globo, che nel prototipo e' SILHOUETTE per meta' altezza: 1,4706
 * radianti per ogni raggio trascinato.
 */
const DRAG = Math.PI * 2.25 * 0.3 * SILHOUETTE;

const DAMPING = 0.045; // dampingFactor del prototipo, per frame a 60Hz
const AUTO_SPEED = ((2 * Math.PI) / 60) * 0.18; // autoRotateSpeed 0.18: rad/s, un giro ogni 5,55 minuti
const AUTO_RESUME = 1800; // ms dopo il rilascio, come il prototipo
const MAX_LAT = 60 * DEG;

/**
 * Un frame di OrbitControls con enableDamping: lo spostamento in sospeso si
 * applica per la quota k e il resto aspetta il frame dopo. k e' lo 0,045 per
 * frame del prototipo riportato al tempo, cosi' a 120Hz non va al doppio.
 * L'auto-rotazione entra nello stesso sospeso, come in OrbitControls: a
 * regime gira esattamente a AUTO_SPEED. lng scende: la superficie va verso est.
 */
function stepRotation(state, dt, auto) {
  if (auto) state.dLng -= AUTO_SPEED * dt;
  const k = 1 - Math.pow(1 - DAMPING, dt * 60);
  state.lng += state.dLng * k;
  state.lat = Math.max(-MAX_LAT, Math.min(MAX_LAT, state.lat + state.dLat * k));
  state.dLng *= 1 - k;
  state.dLat *= 1 - k;
}

// --- WebGL ---

/** Raggio della silhouette nel prototipo, in pixel del device, su una finestra alta 900 a DPR 2. Le luci si scalano su questo. */
const REF_RADIUS = SILHOUETTE * 450 * 2;

/* Lo shader lavora su ogni pixel del canvas: su un 2560px a DPR 2 sarebbero
   8,7 milioni a frame, troppi per una GPU integrata. Si scende di densita'.
   ponytail: tetto fisso, da abbassare se i portatili deboli scattano. */
const MAX_PIXELS = 4e6;

/** Un triangolo che copre lo schermo, senza buffer: i vertici escono da gl_VertexID. */
const QUAD_VS = `#version 300 es
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

/**
 * Sfera e alone per ogni pixel. Camera in (0, 0, D) che guarda -z, sfera di
 * raggio 1 nell'origine, centro del globo in uCenter (pixel del device, dal
 * basso). Illuminazione, alone e colori sono quelli di globe.gl su three r17x:
 * vedi la spec, §6.
 */
const GLOBE_FS = `#version 300 es
precision highp float;
uniform sampler2D uTex;
uniform mat3 uRot;
uniform vec2 uCenter;
uniform float uFocal;
uniform float uTint;
out vec4 outColor;

const float D = ${CAM_DIST.toFixed(4)};
const float GLOW_R = 1.045;
const vec3 GLOW = vec3(0.0, 0.7454, 1.0);
const float PI = 3.141592653589793;

vec3 toSrgb(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}

void main() {
  vec3 o = vec3(0.0, 0.0, D);
  vec3 dir = normalize(vec3(gl_FragCoord.xy - uCenter, -uFocal));
  float b = dot(o, dir);
  float c = dot(o, o);

  // Distanza del raggio dal centro della sfera: 1 e' il bordo. Sul bordo il
  // pixel e' in parte globo e in parte alone, e la copertura li mescola.
  float miss = sqrt(max(c - b * b, 0.0));
  float cover = clamp((1.0 - miss) / fwidth(miss) + 0.5, 0.0, 1.0);

  // La sfera si calcola anche dove il raggio la manca: le derivate per le
  // mipmap vogliono tutti e quattro i pixel del blocco 2x2.
  vec3 n = normalize(uRot * (o + (-b - sqrt(max(b * b - c + 1.0, 0.0))) * dir));
  float lat = asin(clamp(n.y, -1.0, 1.0));
  float lng = atan(n.x, n.z);
  vec2 uv = vec2(0.5 + lng / (2.0 * PI), 0.5 - lat / PI);

  // A ±180° u salta da 1 a 0 e le mipmap disegnerebbero una riga: le
  // derivate si prendono dalla variante che li' non salta (Tarini).
  vec2 uvAlt = vec2(fract(uv.x + 0.5) - 0.5, uv.y);
  vec2 dx = dFdx(uv);
  vec2 dy = dFdy(uv);
  vec2 dxAlt = dFdx(uvAlt);
  vec2 dyAlt = dFdy(uvAlt);
  if (abs(dxAlt.x) + abs(dyAlt.x) < abs(dx.x) + abs(dy.x)) {
    dx = dxAlt;
    dy = dyAlt;
  }

  // Ambiente 0xcccccc·π (0,6038 in lineare) piu' la direzionale 0,6·π da +y,
  // diviso π dal Lambert: la luce viene sempre da nord.
  vec3 lin = textureGrad(uTex, uv, dx, dy).rgb * (0.6038 + 0.6 * max(n.y, 0.0));

  // Lo speculare Blinn-Phong di MeshPhongMaterial: specular 0x111111 (0,0056
  // in lineare), shininess 30, G = 1/4, quindi 0,6·π · 1/4 · 16/π = 2,4. Con
  // la luce da nord e la vista a 18°N il riflesso cade sull'Europa: sembra
  // poco, ma li' il prototipo e' di 17 unita' piu' chiaro.
  vec3 view = normalize(uRot * -dir);
  vec3 halfway = normalize(vec3(0.0, 1.0, 0.0) + view);
  float dotVH = max(dot(view, halfway), 0.0);
  float fresnel = exp2((-5.55473 * dotVH - 6.98316) * dotVH);
  float specular = 0.0056 * (1.0 - fresnel) + fresnel;
  lin += 2.4 * max(n.y, 0.0) * specular * pow(max(dot(n, halfway), 0.0), 30.0);

  vec3 globe = toSrgb(lin);
  // La tinta porta verso l'azzurro del mockup mobile: a 0,5 la media del disco
  // passa da (5, 19, 36) a (5, 38, 85), quella dello screenshot di riferimento.
  globe = clamp(mix(globe, globe * vec3(1.0, 3.0, 3.7), uTint), 0.0, 1.0);

  // Alone: la faccia posteriore della sfera di raggio 1,045, come GlowMesh
  // (coefficient 0.1, power 3.5). Il colore e' #00dfff in lineare, scritto
  // senza riconversione come fa il suo ShaderMaterial.
  float hg = b * b - c + GLOW_R * GLOW_R;
  vec3 back = (o + (-b + sqrt(max(hg, 0.0))) * dir) / GLOW_R;
  float a = step(0.0, hg) * clamp(pow(max(0.1 + dot(back, dir), 0.0), 3.5), 0.0, 1.0);

  outColor = vec4(mix(GLOW * a, globe, cover), 1.0);
}`;

/** Le luci: posizione, lampeggio e dimensione come il prototipo, con la sfera che nasconde quelle dietro. */
const POINTS_VS = `#version 300 es
layout(location = 0) in vec3 aPos;
layout(location = 1) in float aPhase;
layout(location = 2) in float aSpeed;
layout(location = 3) in float aSize;
uniform mat3 uRot;
uniform vec2 uCenter;
uniform vec2 uViewport;
uniform float uFocal;
uniform float uLightScale;
uniform float uTime;
out float vAlpha;

const float D = ${CAM_DIST.toFixed(4)};

void main() {
  // Dalla Terra alla camera: la trasposta.
  vec3 p = transpose(uRot) * aPos;
  vec3 o = vec3(0.0, 0.0, D);
  vec3 ray = p - o;
  float len = length(ray);
  vec3 dir = ray / len;
  float b = dot(o, dir);
  float h = b * b - dot(o, o) + 1.0;

  // Se il raggio colpisce la sfera prima della luce, la luce e' dietro: fuori
  // dal clip space. E' il depthTest del prototipo.
  if (h > 0.0 && -b - sqrt(h) < len) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 1.0;
    vAlpha = 0.0;
    return;
  }

  float z = D - p.z;
  gl_Position = vec4((uCenter + uFocal * p.xy / z) / uViewport * 2.0 - 1.0, 0.0, 1.0);

  float wave = 0.5 + 0.5 * sin(uTime * aSpeed + aPhase);
  float blink = smoothstep(0.22, 0.82, wave);
  vAlpha = 0.05 + blink * 0.85;
  // (0.75 + 1.7·blink)·aSize·105/z del prototipo, z in unita' da 100, scalato col globo.
  gl_PointSize = (0.75 + blink * 1.7) * aSize * (1.05 / z) * uLightScale;
}`;

const POINTS_FS = `#version 300 es
precision mediump float;
in float vAlpha;
out vec4 outColor;

void main() {
  float glow = 1.0 - smoothstep(0.0, 0.5, length(gl_PointCoord - 0.5));
  outColor = vec4(0.62, 0.95, 1.0, glow * vAlpha);
}`;

function compile(gl, vsSource, fsSource) {
  const program = gl.createProgram();
  for (const [type, source] of [[gl.VERTEX_SHADER, vsSource], [gl.FRAGMENT_SHADER, fsSource]]) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS) && !gl.isContextLost()) {
      throw new Error(`[global-countdown] shader: ${gl.getShaderInfoLog(shader)}`);
    }
    gl.attachShader(program, shader);
  }
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS) && !gl.isContextLost()) {
    throw new Error(`[global-countdown] program: ${gl.getProgramInfoLog(program)}`);
  }
  return program;
}

function locate(gl, program, names) {
  const out = { program };
  for (const name of names) out[name] = gl.getUniformLocation(program, name);
  return out;
}

class GlobalCountdown extends HTMLElement {
  connectedCallback() {
    this.initCountdown();

    // Rimesso nella pagina dopo uno smontaggio (l'editor sposta le sezioni nel
    // DOM quando le riordini): disconnectedCallback ha perso apposta il
    // contesto, e il canvas ha ancora i listener di prima. Si riparte da un
    // canvas nuovo, come la prima volta.
    if (this.gl) {
      const fresh = this.canvas.cloneNode(false);
      fresh.classList.remove('is-visible', 'is-dragging');
      this.canvas.replaceWith(fresh);
      Object.assign(this, { image: null, ready: false, drawn: false, raf: 0, pointer: null });
    }

    this.canvas = this.querySelector('[data-gcd-canvas]');
    this.globeBox = this.querySelector('[data-gcd-globe]');
    this.gl = this.canvas.getContext('webgl2', { alpha: false, antialias: false });
    // Senza WebGL2 resta tutto il resto: titolo, contatore e CTA sono HTML.
    if (!this.gl) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.tint = parseFloat(this.dataset.tint) || 0;
    this.lights = buildLights();
    this.rot = { lat: 18 * DEG, lng: 15 * DEG, dLat: 0, dLng: 0 };
    this.auto = !this.reduced;
    this.time = 0;
    this.frame = this.frame.bind(this);

    // preventDefault dice al browser che il contesto lo vogliamo indietro.
    this.canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      this.stop();
      this.ready = false;
    });
    this.canvas.addEventListener('webglcontextrestored', () => {
      if (this.image && this.image.complete) this.initGL();
    });

    this.bindPointer();

    this.resizer = new ResizeObserver(() => this.measure());
    this.resizer.observe(this.canvas);

    // Texture e shader solo quando la sezione si avvicina; fuori dallo
    // schermo il rAF si ferma. Le schede in background le ferma gia' il browser.
    this.observer = new IntersectionObserver(
      (entries) => {
        this.visible = entries[0].isIntersecting;
        if (!this.visible) return this.stop();
        this.load();
        this.start();
      },
      { rootMargin: '200px 0px' },
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
    clearTimeout(this.resume);
    this.stop();
    if (this.observer) this.observer.disconnect();
    if (this.resizer) this.resizer.disconnect();
    // L'editor ridisegna la sezione a ogni modifica: senza, i contesti si
    // accumulano finche' il browser comincia a chiudere i piu' vecchi.
    const lose = this.gl && this.gl.getExtension('WEBGL_lose_context');
    if (lose) lose.loseContext();
  }

  initCountdown() {
    this.timer = this.querySelector('[data-gcd-timer]');
    // Countdown spento: il testo l'ha gia' scritto la Liquid, insieme a is-ended.
    if (!this.timer) return;

    this.nums = [...this.timer.querySelectorAll('[data-gcd-num]')];
    this.target = parseExpires(this.dataset.expires);
    if (Number.isNaN(this.target)) {
      console.warn('[global-countdown] scadenza non valida:', this.dataset.expires);
      return;
    }

    if (!this.tick()) {
      this.interval = setInterval(() => {
        if (this.tick()) clearInterval(this.interval);
      }, 250);
    }

    // Se era gia' scaduto, is-ended e' appena entrata: la si fa calcolare
    // prima di accendere le transizioni, o il testo comparirebbe in
    // dissolvenza a ogni apertura della pagina.
    void this.offsetHeight;
    this.classList.add('is-ready');
  }

  /** Scrive le cifre che cambiano e restituisce true quando il tempo e' finito. */
  tick() {
    const { d, h, m, s } = remaining(this.target, Date.now());
    [d, h, m, s].forEach((value, i) => {
      const text = pad(value);
      if (this.nums[i].textContent !== text) this.nums[i].textContent = text;
    });
    if (d + h + m + s > 0) return false;
    this.end();
    return true;
  }

  end() {
    const template = this.querySelector('[data-gcd-end-template]');
    // Senza testo il contatore resta a zero. Il template se ne va al primo
    // giro, quindi un secondo end() non duplica il testo.
    if (!template) return;
    this.querySelector('[data-gcd-end]').append(template.content.cloneNode(true));
    template.remove();
    this.classList.add('is-ended');
  }

  /** Canvas, centro e raggio del globo: il cerchio lo decide il CSS con .gcd__globe. */
  measure() {
    const stage = this.canvas.getBoundingClientRect();
    const box = this.globeBox.getBoundingClientRect();
    if (!stage.width || !stage.height) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(MAX_PIXELS / (stage.width * stage.height)));
    // Per difetto: arrotondando per eccesso il tetto si sforerebbe di qualche pixel.
    this.canvas.width = Math.floor(stage.width * dpr);
    this.canvas.height = Math.floor(stage.height * dpr);

    this.radiusCss = box.width / 2;
    this.radius = this.radiusCss * dpr;
    // La focale che fa coincidere la silhouette della sfera con il box.
    this.focal = this.radius * Math.sqrt(CAM_DIST * CAM_DIST - 1);
    // gl_FragCoord parte dal basso a sinistra.
    this.center = [(box.left + box.width / 2 - stage.left) * dpr, (stage.bottom - box.top - box.height / 2) * dpr];

    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    // Cambiare la misura svuota il canvas: col rAF fermo resterebbe nero.
    if (this.ready) this.draw();
  }

  load() {
    if (this.image) return;
    const small = window.matchMedia('(max-width: 767px)').matches || this.gl.getParameter(this.gl.MAX_TEXTURE_SIZE) < 4096;
    this.image = new Image();
    this.image.crossOrigin = 'anonymous';
    this.image.onload = () => this.initGL();
    // L'immagine del merchant puo' non avere CORS o un URL rotto: allora una
    // volta sola quella del tema, che sta sullo stesso dominio.
    this.image.onerror = () => {
      this.image.onerror = null;
      this.image.src = this.dataset.texFallback;
    };
    this.image.src = small ? this.dataset.texMobile : this.dataset.tex;
  }

  /** Programmi, texture e luci. Gira anche dopo webglcontextrestored, con l'immagine gia' in mano. */
  initGL() {
    const gl = this.gl;
    if (gl.isContextLost()) return;

    this.globe = locate(gl, compile(gl, QUAD_VS, GLOBE_FS), ['uRot', 'uCenter', 'uFocal', 'uTint']);
    this.points = locate(gl, compile(gl, POINTS_VS, POINTS_FS), ['uRot', 'uCenter', 'uViewport', 'uFocal', 'uLightScale', 'uTime']);

    gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
    // SRGB8_ALPHA8: il campionamento restituisce gia' valori lineari, come la texture sRGB di three.
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.SRGB8_ALPHA8, gl.RGBA, gl.UNSIGNED_BYTE, this.image);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    this.vao = gl.createVertexArray();
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, this.lights, gl.STATIC_DRAW);
    // x, y, z, fase, velocita', dimensione: sei float, 24 byte per luce.
    [3, 1, 1, 1].reduce((offset, size, location) => {
      gl.enableVertexAttribArray(location);
      gl.vertexAttribPointer(location, size, gl.FLOAT, false, 24, offset * 4);
      return offset + size;
    }, 0);
    gl.bindVertexArray(null);

    gl.blendFunc(gl.SRC_ALPHA, gl.ONE); // l'AdditiveBlending di three
    this.ready = true;
    this.measure();
    this.start();
  }

  start() {
    if (!this.ready || !this.visible || this.raf) return;
    this.last = 0;
    this.raf = requestAnimationFrame(this.frame);
  }

  stop() {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  frame(now) {
    this.raf = requestAnimationFrame(this.frame);
    // Dopo una pausa il primo dt sarebbe enorme: al massimo un decimo di secondo.
    const dt = this.last ? Math.min((now - this.last) / 1000, 0.1) : 0;
    this.last = now;
    stepRotation(this.rot, dt, this.auto);
    // Con il movimento ridotto il tempo resta fermo e ogni luce al suo livello.
    if (!this.reduced) this.time += dt;
    this.draw();
  }

  draw() {
    const gl = this.gl;
    const rot = rotationMatrix(this.rot.lat, this.rot.lng);

    gl.disable(gl.BLEND);
    gl.useProgram(this.globe.program);
    gl.uniformMatrix3fv(this.globe.uRot, false, rot);
    gl.uniform2fv(this.globe.uCenter, this.center);
    gl.uniform1f(this.globe.uFocal, this.focal);
    gl.uniform1f(this.globe.uTint, this.tint);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    gl.enable(gl.BLEND);
    gl.useProgram(this.points.program);
    gl.uniformMatrix3fv(this.points.uRot, false, rot);
    gl.uniform2fv(this.points.uCenter, this.center);
    gl.uniform2f(this.points.uViewport, this.canvas.width, this.canvas.height);
    gl.uniform1f(this.points.uFocal, this.focal);
    gl.uniform1f(this.points.uLightScale, this.radius / REF_RADIUS);
    gl.uniform1f(this.points.uTime, this.time);
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.POINTS, 0, this.lights.length / 6);
    gl.bindVertexArray(null);

    if (!this.drawn) {
      this.drawn = true;
      this.canvas.classList.add('is-visible');
    }
  }

  /**
   * Gli OrbitControls del prototipo, senza zoom: il trascinamento va nel
   * sospeso di stepRotation, quindi segue il dito con lo stesso smorzamento e
   * continua per inerzia dopo il rilascio. "Afferra e trascina": verso destra,
   * la superficie va a destra.
   */
  bindPointer() {
    this.canvas.addEventListener('pointerdown', (event) => {
      if (this.pointer != null) return;
      this.pointer = event.pointerId;
      this.touch = event.pointerType === 'touch';
      this.px = event.clientX;
      this.py = event.clientY;
      this.canvas.setPointerCapture(event.pointerId);
      this.canvas.classList.add('is-dragging');
      this.auto = false;
      clearTimeout(this.resume);
    });

    this.canvas.addEventListener('pointermove', (event) => {
      if (event.pointerId !== this.pointer) return;
      const k = DRAG / this.radiusCss;
      this.rot.dLng -= (event.clientX - this.px) * k;
      // Sul touch il verticale e' dello scroll della pagina (touch-action: pan-y).
      if (!this.touch) this.rot.dLat += (event.clientY - this.py) * k;
      this.px = event.clientX;
      this.py = event.clientY;
    });

    // pointercancel arriva quando il browser si prende il gesto per scorrere.
    const release = (event) => {
      if (event.pointerId !== this.pointer) return;
      this.pointer = null;
      this.canvas.classList.remove('is-dragging');
      if (!this.reduced) this.resume = setTimeout(() => (this.auto = true), AUTO_RESUME);
    };
    this.canvas.addEventListener('pointerup', release);
    this.canvas.addEventListener('pointercancel', release);
  }
}

if (!customElements.get('global-countdown')) customElements.define('global-countdown', GlobalCountdown);
